import React from "react";

import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import { withTranslationsProvider } from "@narvar/nth-hook-translations";
import { ItemCard, Language } from "../";
import {
  ITEM_CARD_LABELS_TRANSLATION_TOKENS,
  useItemCardLabels,
} from "../../../ItemSelector/helper";
import { ExchangeVariant } from "../../../ReturnReasons/types";
import { getBorderColor } from "../../transformers/style";
import { getItemAttrs, getItemProps } from "../../utils/itemSelectorHelper";
import LineItemGroupHeader from "./LineItemGroupHeader";
import { LineItemsGroupProps } from "./types";

enum GroupingErrorType {
  VIOLATE_ONE_GROUP_PER_RETURN = "VIOLATE_ONE_GROUP_PER_RETURN",
}

const LineItemsGroupC: React.FC<LineItemsGroupProps> = ({
  group,
  selectedGroup,
  itemsWereSelected,
  items,
  selectedItems,
  itemDisplay,
  locale,
  includeCurrency,
  onItemClick,
}) => {
  const {
    bodytextDefaultNone,
    buttonsPrimaryDefault,
    documentbodyNoneNone,
    panelcontainerNoneNone,
    selecteditemNoneNone,
  } = useTheme();
  const { skuLabel, priceLabel, qtyLabel, colorLabel, sizeLabel, reasonLabel } =
    useItemCardLabels();

  let error: GroupingErrorType | null = null;
  if (
    itemsWereSelected &&
    !selectedGroup &&
    group?.behaviors?.includes("one_group_per_return")
  ) {
    error = GroupingErrorType.VIOLATE_ONE_GROUP_PER_RETURN;
  }
  const clickable = error === null;
  const disabled = !clickable;

  if (items.length === 0) return null;

  return (
    <>
      {error && (
        <div
          style={{
            ...bodytextDefaultNone,
            margin: "1.5rem auto 1rem auto",
          }}>
          <Language langKey="returnsLineItemGroupIneligibleDescription" />
        </div>
      )}
      <LineItemGroupHeader title={group?.title} />
      {items.map((item) => {
        const selectedItem = selectedItems?.find(
          ({ sku, itemId, localId }) =>
            item.sku === sku &&
            item.itemId === itemId &&
            item.localId === localId,
        );
        const selectedReason =
          selectedItem &&
          item.availableReasons?.find(
            (reason) => reason.id === selectedItem.reason.id,
          );
        const exchangeTo: ExchangeVariant | undefined =
          selectedItem && "exchangeTo" in selectedItem
            ? selectedItem.exchangeTo
            : undefined;

        return (
          <ItemCard
            key={`${item.sku ?? ""}${item.itemId ?? ""}${item.localId ?? 0}`}
            {...getItemProps({
              item,
              exchangeTo,
              isClaim: selectedItem?.isClaim,
            })}
            attributes={getItemAttrs({
              locale,
              includeCurrency,
              item: {
                ...item,
                quantity: item.remainingQuantity,
              },
              itemDisplay,
              selectedQuantity: selectedItem?.quantity,
              labels: {
                skuLabel,
                priceLabel,
                qtyLabel,
                colorLabel,
                sizeLabel,
              },
              exchangeTo,
            })}
            additionalInfo={
              selectedReason &&
              `${reasonLabel ?? ""}${selectedReason?.name ?? ""}`
            }
            hasShippingProtection={item.hasShippingProtection}
            onClick={() => onItemClick?.(item)}
            selected={Boolean(selectedItem)}
            clickable={clickable}
            disabled={disabled}
            sDefault={{
              ...panelcontainerNoneNone,
              backgroundColor: documentbodyNoneNone?.backgroundColor,
            }}
            sActive={selecteditemNoneNone}
            sTag={{
              ...selecteditemNoneNone,
              borderRadius: 0,
              borderTopLeftRadius:
                selecteditemNoneNone?.borderTopLeftRadius ??
                selecteditemNoneNone?.borderRadius ??
                4,
              borderBottomRightRadius:
                selecteditemNoneNone?.borderBottomRightRadius ??
                selecteditemNoneNone?.borderRadius ??
                3,
              backgroundColor:
                getBorderColor(selecteditemNoneNone) ??
                buttonsPrimaryDefault?.backgroundColor,
            }}
          />
        );
      })}
    </>
  );
};

export const LineItemsGroup = withTranslationsProvider(
  withThemeProvider(LineItemsGroupC, {
    themeTokens: [
      "bodytextDefaultNone",
      "buttonsPrimaryDefault",
      "buttonsPrimaryHover",
      "documentbodyNoneNone",
      "panelcontainerNoneNone",
      "selecteditemNoneNone",
    ],
  }),
  {
    translationKeys: ITEM_CARD_LABELS_TRANSLATION_TOKENS,
  },
);
